import { Cookie } from "./Cookie";
import { Statuses } from "../domain";
import { AuthTokenData, AuthTokenDecoded, DefaultAuthToken } from "../models/auth";
import { BasicProfile } from "../models/user";
import Settings from "../settings";
import { AuthState } from "../store/AuthState";

interface TokenStorageObject {
    accessToken: string;
    refreshToken: string;
}

export const clearLegacyAuthCookie = async () => {
    return new Promise((resolve, reject) => {
        Cookie.clear(Settings.LegacyAuthCookie, Settings.CookieDomain);
        resolve({});
    });
};

/**
 * Clears the token data from storage
 */
export const clearTokenFromCookie = (): void => {
    Cookie.set(Settings.TokenCookie, "", -1 * 24 * 60 * 60 * 1000, Settings.CookieDomain);
};

/**
 * Retrieves token data from storage
 */
export const getTokenFromCookie = (): TokenStorageObject | undefined => {
    return Cookie.getAs<TokenStorageObject>(Settings.TokenCookie);
};

/**
 * Saves the access/refresh tokens into storage for later retrieval.
 * @param accessToken The access token
 * @param refreshToken The refresh token
 */
export const saveTokenToCookie = (accessToken: string, refreshToken: string) => {
    const tokenObj: TokenStorageObject = {
        accessToken,
        refreshToken,
    };

    const tokenObjStr = JSON.stringify(tokenObj);
    Cookie.set(Settings.TokenCookie, tokenObjStr, 30 * 24 * 60 * 60 * 1000, Settings.CookieDomain); // 30 days
};

export const getAuthTokenData = (auth: AuthState) => {
    const token = new AuthTokenDecoded(auth.accessToken || "", auth.refreshToken || "");
    if (token.isValid)
        return token;
    return undefined;
};

/**
 * Determines whether the user record of the provided access token is an admin user or not
 * @param accessToken The access token
 * @param refreshToken The refresh token
 */
export const isAdminUser = (accessToken?: string, refreshToken?: string): boolean => {
    if (accessToken && refreshToken) {
        const decodedToken =  new AuthTokenDecoded(accessToken, refreshToken);
        return decodedToken.hasAdminRole();
    }
    return false;
};

export const tokenData = (auth: AuthState) => {
    const userToken = getAuthTokenData(auth);
    return (userToken && userToken.tokenData) || DefaultAuthToken;
};

export const getBasicUserProfile = (token: AuthTokenData) => {
    const user: BasicProfile = {
        degrees: token.userTitle,
        firstName: token.userFirstName,
        guid: token.userGuid || "",
        id: token.userId || 0,
        isMember: token.isMember,
        lastName: token.userLastName,
        legacyMemberGuid: null,
        legacyMemberId: null,
        legacyUserId: null,
        profilePicture: token.userProfilePicture,
        requireSessionRefresh: token.requireSessionRefresh,
        requiresNewAccount: token.requiresNewAccount,
        statusId: Statuses.Active.id,
        username: token.username || "",
    };
    return user;
};



// WEBPACK FOOTER //
// ./src/utils/AuthHelper.ts